import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import { ReactComponent as ArrowLeft } from 'src/Images/icons/common/arrowleft.svg';
import { ReactComponent as ArrowRight } from 'src/Images/icons/common/arrowright.svg';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { ReactComponent as FilterIcon } from 'src/Images/icons/common/funnel.svg';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import {
  getDeliveryDropdownOptions,
  getDeliveryForBusinessPartner,
  getCategoryBusinessPartnersDropdownOptions,
} from 'src/redux/Actions/VehicleTrackingOutward';

function VehicleTrackingDeliveryEditModal({
  themeColor,
  selectedDeliveryObjList,
  setSelectedDeliveryObjList,
  deliveryHeaderData,
  toggleEditDrawer,
  handleUpdateDeliverySubmit,
  isSubmitting,
}) {
  const dispatch = useDispatch();

  const authReducer = useSelector((state) => state.AuthReducer);
  const organisationId = authReducer.userData.organisations?.id;

  const vehicleTrackingDataReducer = useSelector(
    (state) => state?.VehicleTrackingReducer,
  );
  const vehicleNumber =
    vehicleTrackingDataReducer?.vehicleTrackingDetails?.vehicleNumber;

  const vehicleTrackingOutwardReducer = useSelector(
    (state) => state?.VehicleTrackingOutwardReducer,
  );
  const { categoryBasedBusinessPartner } = vehicleTrackingOutwardReducer;
  const [activeTab, setActiveTab] = useState('search');
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const today = new Date().toISOString().split('T')[0];
  const [filterFormData, setFilterFormData] = useState({
    vehicleNumber: vehicleNumber || '',
    deliveryNumber: '',
    productSapCode: '',
    businessPartnerId: null,
    deliveryStartDate: today,
    deliveryEndDate: today,
  });

  const [businessPartnerSearchTerm, setBusinessPartnerSearchTerm] =
    useState('');
  const [drawerSearchPageNumber, setDrawerSearchPageNumber] = useState(1);
  const [deliveryList, setDeliveryList] = useState(null);
  const deliveryTotalPages = deliveryList?.totalPages || 1;

  const businessPartnersWithDeliveryOptions = (
    categoryBasedBusinessPartner || []
  ).map((partner) => ({
    value: partner.id,
    label: partner.name,
  }));

  const handleSearchFormChange = (field, value) => {
    setFilterFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDeliveryNumberInputChange = (e) => {
    handleSearchFormChange('deliveryNumber', e.target.value);
  };

  const handleProductInputChange = (e) => {
    handleSearchFormChange('productSapCode', e.target.value);
  };

  const handleBusinessPartnerSelectChange = (selectedOption) => {
    setBusinessPartnerSearchTerm('');
    handleSearchFormChange('businessPartnerId', selectedOption?.value || null);
  };

  const handleSearchSubmit = (page = 1) => {
    if (!organisationId) return;

    setDrawerSearchPageNumber(page);

    if (filterFormData.businessPartnerId) {
      dispatch(
        getDeliveryForBusinessPartner({
          tenant_organisation_id: organisationId,
          business_partner_id: filterFormData.businessPartnerId,
          search_term: filterFormData.deliveryNumber || '',
          page_number: page,
        }),
      )
        .then((response) => {
          const responseBody = response?.data || response || {};
          setDeliveryList({
            delivery: responseBody.delivery || [],
            totalPages: responseBody.totalPages || 1,
          });
        })
        .catch(() => {
          setDeliveryList(null);
        });
    } else {
      dispatch(
        getDeliveryDropdownOptions(
          organisationId,
          filterFormData.vehicleNumber,
          page,
          10,
          filterFormData.deliveryStartDate,
          filterFormData.deliveryEndDate,
          filterFormData.productSapCode || null,
          filterFormData.deliveryNumber || null,
        ),
      )
        .then((response) => {
          const responseBody = response?.data || response || {};
          setDeliveryList({
            delivery: responseBody.delivery || [],
            totalPages: responseBody.totalPages || 1,
          });
        })
        .catch(() => {
          setDeliveryList(null);
        });
    }
  };

  useEffect(
    () => () => {
      setDeliveryList(null);
    },
    [],
  );

  const handleCancel = () => {
    setDeliveryList(null);
    toggleEditDrawer(false)();
  };

  useEffect(() => {
    if (!organisationId) return undefined;
    dispatch(
      getCategoryBusinessPartnersDropdownOptions(
        organisationId,
        businessPartnerSearchTerm,
        '1',
      ),
    );
    return undefined;
  }, [organisationId, businessPartnerSearchTerm, dispatch]);

  const formattedDrawerPageNumber =
    drawerSearchPageNumber < 10
      ? `0${drawerSearchPageNumber}`
      : drawerSearchPageNumber;

  const handleDrawerPreviousPage = () => {
    if (drawerSearchPageNumber > 1) {
      handleSearchSubmit(drawerSearchPageNumber - 1);
    }
  };

  const handleDrawerNextPage = () => {
    if (drawerSearchPageNumber < deliveryTotalPages) {
      handleSearchSubmit(drawerSearchPageNumber + 1);
    }
  };

  const handleDrawerPageInputChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (Number.isNaN(value)) return;

    if (value >= 1 && value <= deliveryTotalPages) {
      handleSearchSubmit(value);
    }
  };

  const getTabClassName = (tabKey) =>
    activeTab === tabKey
      ? `theme-pill-active-${themeColor}`
      : 'border border-radius-4 vertical-padding-5 horizontal-padding-10 text-grey';

  const isDeliverySelected = (deliveryObj) =>
    selectedDeliveryObjList.some(
      (selectedDeliveryObj) =>
        selectedDeliveryObj.delivery_id === deliveryObj.delivery_id,
    );

  const isLoadOnDeliveryConfirmed = (deliveryObj) => {
    const attachedMatch = deliveryHeaderData.find(
      (attachedObj) => attachedObj.delivery_id === deliveryObj.delivery_id,
    );
    return Boolean(attachedMatch?.has_confirmed_load);
  };

  const handleToggleDeliverySelection = (deliveryObj, checked) => {
    if (isLoadOnDeliveryConfirmed(deliveryObj)) return;

    setSelectedDeliveryObjList((prev) => {
      if (checked) {
        const alreadySelected = prev.some(
          (selectedDeliveryObj) =>
            selectedDeliveryObj.delivery_id === deliveryObj.delivery_id,
        );
        return alreadySelected ? prev : [...prev, deliveryObj];
      }

      return prev.filter(
        (selectedDeliveryObj) =>
          selectedDeliveryObj.delivery_id !== deliveryObj.delivery_id,
      );
    });
  };

  const handleSelectAllVisible = (visibleDeliveryObjList, checked) => {
    setSelectedDeliveryObjList((prev) => {
      const selectableDeliveryList = visibleDeliveryObjList.filter(
        (deliveryObj) => !isLoadOnDeliveryConfirmed(deliveryObj),
      );

      if (checked) {
        const next = [...prev];
        selectableDeliveryList.forEach((deliveryObj) => {
          const alreadySelected = next.some(
            (selectedDeliveryObj) =>
              selectedDeliveryObj.delivery_id === deliveryObj.delivery_id,
          );
          if (!alreadySelected) {
            next.push(deliveryObj);
          }
        });
        return next;
      }

      return prev.filter(
        (selectedDeliveryObj) =>
          !selectableDeliveryList.some(
            (deliveryObj) =>
              deliveryObj.delivery_id === selectedDeliveryObj.delivery_id,
          ),
      );
    });
  };

  const selectableDeliveryObjList = (deliveryList?.delivery || []).filter(
    (deliveryObj) => !isLoadOnDeliveryConfirmed(deliveryObj),
  );
  const isAllDeliveryObjListSelected =
    selectableDeliveryObjList.length > 0 &&
    selectableDeliveryObjList.every((deliveryObj) =>
      isDeliverySelected(deliveryObj),
    );

  const isAllAttachedDeliveryObjSelected =
    selectedDeliveryObjList.filter(
      (deliveryObj) => !isLoadOnDeliveryConfirmed(deliveryObj),
    ).length > 0 &&
    selectedDeliveryObjList
      .filter((deliveryObj) => !isLoadOnDeliveryConfirmed(deliveryObj))
      .every((deliveryObj) => isDeliverySelected(deliveryObj));

  return (
    <>
      <div className="div-flex-row cg-10 border-bottom padding-bottom-10 text-sm">
        <button
          type="button"
          className={getTabClassName('search')}
          onClick={() => setActiveTab('search')}
        >
          Search
        </button>
        <button
          type="button"
          className={getTabClassName('attached')}
          onClick={() => setActiveTab('attached')}
        >
          Attached
        </button>
      </div>

      {activeTab === 'search' && (
        <div className="div-flex-column rg-10">
          {!isFilterExpanded ? (
            <div className="input-wrapper">
              <input
                className="form-input-section margin-top-5"
                type="text"
                name="delivery-number"
                placeholder="Search Delivery"
                value={filterFormData.deliveryNumber}
                onChange={handleDeliveryNumberInputChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchSubmit();
                  }
                }}
              />
              <button
                type="button"
                className="form-input-section-icon icon-btn-none"
                onClick={() => setIsFilterExpanded(true)}
                aria-label="Show filters"
              >
                <FilterIcon className="icon-size-sm" color="#9F9F9F" />
              </button>
            </div>
          ) : (
            <>
              <Row className="w-100">
                <Col xxl={3} xl={3} lg={3} md={6} sm={12}>
                  <div className="div-flex-column">
                    <span className="text-sm text-bold">Vehicle Number</span>
                    <input
                      type="text"
                      className="form-input-section margin-top-5"
                      value={filterFormData.vehicleNumber}
                      onChange={(e) =>
                        handleSearchFormChange('vehicleNumber', e.target.value)
                      }
                      placeholder="Vehicle Number"
                    />
                  </div>
                </Col>

                <Col xxl={3} xl={3} lg={3} md={6} sm={12}>
                  <div className="div-flex-column">
                    <span className="text-sm text-bold">Delivery Number</span>
                    <div className="input-wrapper">
                      <input
                        className="form-input-section margin-top-5"
                        type="text"
                        placeholder="Search Delivery Number"
                        value={filterFormData.deliveryNumber}
                        onChange={handleDeliveryNumberInputChange}
                      />
                      <span className="form-input-section-icon">
                        <Search className="icon-size-sm" color="#9F9F9F" />
                      </span>
                    </div>
                  </div>
                </Col>

                <Col xxl={3} xl={3} lg={3} md={6} sm={12}>
                  <div className="div-flex-column">
                    <span className="text-sm text-bold">Product</span>
                    <div className="input-wrapper">
                      <input
                        className="form-input-section margin-top-5"
                        type="text"
                        placeholder="Search Delivery Product"
                        value={filterFormData.productSapCode}
                        onChange={handleProductInputChange}
                      />
                      <span className="form-input-section-icon">
                        <Search className="icon-size-sm" color="#9F9F9F" />
                      </span>
                    </div>
                  </div>
                </Col>

                <Col xxl={3} xl={3} lg={3} md={6} sm={12}>
                  <div className="div-flex-column">
                    <span className="text-sm text-bold">Business Partner</span>
                    <div className="input-wrapper">
                      <CustomMultiSelect
                        name="business-partner"
                        isMulti={false}
                        className="multi-select-input-section margin-top-5"
                        onChange={(selectedOption) => {
                          setBusinessPartnerSearchTerm('');
                          handleBusinessPartnerSelectChange(selectedOption);
                        }}
                        onInputChange={(inputValue, { action }) => {
                          if (action === 'input-change') {
                            setBusinessPartnerSearchTerm(inputValue);
                          }
                        }}
                        inputValue={businessPartnerSearchTerm}
                        options={businessPartnersWithDeliveryOptions}
                        classNamePrefix="react-select"
                        placeholder="Search Business Partner"
                        value={businessPartnersWithDeliveryOptions.find(
                          (option) =>
                            option.value === filterFormData.businessPartnerId,
                        )}
                        filterOption={() => true}
                      />
                      <span className="form-input-section-icon">
                        <Search className="icon-size-sm" color="#9F9F9F" />
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="w-100 mt-2 align-items-end">
                <Col xxl={3} xl={3} lg={3} md={6} sm={12}>
                  <div className="div-flex-column">
                    <span className="text-sm text-bold">
                      Delivery Start Date
                    </span>
                    <input
                      type="date"
                      className="form-input-section margin-top-5"
                      value={filterFormData.deliveryStartDate}
                      onChange={(e) =>
                        handleSearchFormChange(
                          'deliveryStartDate',
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </Col>

                <Col xxl={3} xl={3} lg={3} md={6} sm={12}>
                  <div className="div-flex-column">
                    <span className="text-sm text-bold">Delivery End Date</span>
                    <input
                      type="date"
                      className="form-input-section margin-top-5"
                      value={filterFormData.deliveryEndDate}
                      onChange={(e) =>
                        handleSearchFormChange(
                          'deliveryEndDate',
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </Col>

                <Col
                  xxl={6}
                  xl={6}
                  lg={6}
                  md={12}
                  sm={12}
                  className="d-flex justify-content-end align-items-end"
                >
                  <div className="delivery-search-actions">
                    <button
                      type="button"
                      className={`vehicle-tracking-detail-add-vehcile-data-btn theme-text-${themeColor} theme-border-color-${themeColor}`}
                      onClick={() => setIsFilterExpanded(false)}
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className={`vehicle-tracking-detail-move-stage-btn div-align-center theme-background-${themeColor}`}
                      onClick={() => handleSearchSubmit(1)}
                    >
                      Search
                    </button>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </div>
      )}

      {activeTab === 'search' && (
        <div
          className={`po-table-wrapper po-table-scroll-container po-table-scroll-area theme-scrollable-container-${themeColor}`}
        >
          <table>
            <thead className="outward-delivery-table-head">
              <tr>
                <th aria-label="Select delivery">
                  <input
                    type="checkbox"
                    checked={isAllDeliveryObjListSelected}
                    onChange={(e) =>
                      handleSelectAllVisible(
                        selectableDeliveryObjList,
                        e.target.checked,
                      )
                    }
                    disabled={selectableDeliveryObjList.length === 0}
                    aria-label="Select all deliveries"
                  />
                </th>
                <th>Delivery Number</th>
                <th>Delivery Date</th>
                <th>Pack Type</th>
                <th>Pack Quantity</th>
                <th>Grade</th>
                <th>Gross Weight</th>
                <th>Product SAP Code</th>
                <th>Product Description</th>
                <th>Plant</th>
                <th>Net Weight</th>
                <th>Batch</th>
                <th>Sales Order Number</th>
              </tr>
            </thead>
            <tbody>
              {deliveryList?.delivery?.length === 0 ? (
                <tr>
                  <td
                    colSpan={13}
                    className="no-data-found-message border-bottom"
                  >
                    <span className="text-sm">
                      No Delivery Found. Try searching above.
                    </span>
                  </td>
                </tr>
              ) : (
                deliveryList?.delivery?.map((deliveryObj) => {
                  const deliveryLineItems = deliveryObj.line_items || [];
                  const tableRows = deliveryLineItems.length
                    ? deliveryLineItems
                    : [{}];
                  const rowSpan = tableRows.length;
                  const locked = isLoadOnDeliveryConfirmed(deliveryObj);
                  const checked = isDeliverySelected(deliveryObj);

                  const rows = tableRows.map((item, itemIndex) => (
                    <tr
                      key={
                        item?.delivery_line_item_id ||
                        `${deliveryObj.delivery_id}-${itemIndex}`
                      }
                    >
                      {itemIndex === 0 && (
                        <td rowSpan={rowSpan}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={locked}
                            aria-label={
                              locked
                                ? 'Delivery selection locked'
                                : 'Select delivery'
                            }
                            onChange={(e) =>
                              handleToggleDeliverySelection(
                                deliveryObj,
                                e.target.checked,
                              )
                            }
                          />
                        </td>
                      )}
                      {itemIndex === 0 && (
                        <>
                          <td rowSpan={rowSpan}>
                            {deliveryObj?.sap_code ?? '-'}
                          </td>
                          <td rowSpan={rowSpan}>
                            {deliveryObj?.delivery_date ?? '-'}
                          </td>
                          <td rowSpan={rowSpan}>
                            {deliveryObj?.pack_type ?? '-'}
                          </td>
                          <td rowSpan={rowSpan}>
                            {deliveryObj?.pack_quantity ?? '-'}
                          </td>
                          <td rowSpan={rowSpan}>{deliveryObj?.grade ?? '-'}</td>
                          <td rowSpan={rowSpan}>
                            {deliveryObj?.weight ?? '-'}
                          </td>
                        </>
                      )}
                      <td>{item?.product?.product_sap_code ?? '-'}</td>
                      <td>
                        {item?.product_description ||
                          item?.product?.product_description ||
                          '-'}
                      </td>
                      <td>{item?.plant?.name ?? '-'}</td>
                      <td>{item?.quantity ?? '-'}</td>
                      <td>{item?.batch ?? '-'}</td>
                      {itemIndex === 0 && (
                        <td rowSpan={rowSpan}>
                          {deliveryObj?.sales_order_number ?? '-'}
                        </td>
                      )}
                    </tr>
                  ));

                  return rows;
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'attached' && (
        <div
          className={`po-table-wrapper po-table-scroll-container po-table-scroll-area theme-scrollable-container-${themeColor}`}
        >
          <table>
            <thead className="outward-delivery-table-head">
              <tr>
                <th aria-label="Select delivery">
                  <input
                    type="checkbox"
                    checked={isAllAttachedDeliveryObjSelected}
                    onChange={(e) =>
                      handleSelectAllVisible(
                        selectedDeliveryObjList.filter(
                          (deliveryObj) =>
                            !isLoadOnDeliveryConfirmed(deliveryObj),
                        ),
                        e.target.checked,
                      )
                    }
                    disabled={
                      selectedDeliveryObjList.filter(
                        (deliveryObj) =>
                          !isLoadOnDeliveryConfirmed(deliveryObj),
                      ).length === 0
                    }
                    aria-label="Select all deliveries"
                  />
                </th>
                <th>Delivery Number</th>
                <th>Delivery Date</th>
                <th>Pack Type</th>
                <th>Pack Quantity</th>
                <th>Grade</th>
                <th>Gross Weight</th>
                <th>Product SAP Code</th>
                <th>Product Description</th>
                <th>Plant</th>
                <th>Net Weight</th>
                <th>Batch</th>
                <th>Sales Order Number</th>
              </tr>
            </thead>
            <tbody>
              {selectedDeliveryObjList.length === 0 ? (
                <tr>
                  <td
                    colSpan={13}
                    className="no-data-found-message border-bottom"
                  >
                    <span className="text-sm">No Delivery Attached Yet</span>
                  </td>
                </tr>
              ) : (
                selectedDeliveryObjList.map((deliveryObj) => {
                  const deliveryLineItems = deliveryObj.line_items || [];
                  const tableRows = deliveryLineItems.length
                    ? deliveryLineItems
                    : [{}];
                  const rowSpan = tableRows.length;
                  const locked = isLoadOnDeliveryConfirmed(deliveryObj);
                  const checked = isDeliverySelected(deliveryObj);

                  const rows = tableRows.map((item, itemIndex) => (
                    <tr
                      key={
                        item?.delivery_line_item_id ||
                        `${deliveryObj.delivery_id}-${itemIndex}`
                      }
                    >
                      {itemIndex === 0 && (
                        <td rowSpan={rowSpan}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={locked}
                            aria-label={
                              locked
                                ? 'Delivery selection locked'
                                : 'Select delivery'
                            }
                            onChange={(e) =>
                              handleToggleDeliverySelection(
                                deliveryObj,
                                e.target.checked,
                              )
                            }
                          />
                        </td>
                      )}
                      {itemIndex === 0 && (
                        <>
                          <td rowSpan={rowSpan}>
                            {deliveryObj?.sap_code ?? '-'}
                          </td>
                          <td rowSpan={rowSpan}>
                            {deliveryObj?.delivery_date ?? '-'}
                          </td>
                          <td rowSpan={rowSpan}>
                            {deliveryObj?.pack_type ?? '-'}
                          </td>
                          <td rowSpan={rowSpan}>
                            {deliveryObj?.pack_quantity ?? '-'}
                          </td>
                          <td rowSpan={rowSpan}>{deliveryObj?.grade ?? '-'}</td>
                          <td rowSpan={rowSpan}>
                            {deliveryObj?.weight ?? '-'}
                          </td>
                        </>
                      )}
                      <td>{item?.product?.product_sap_code ?? '-'}</td>
                      <td>
                        {item?.product_description ||
                          item?.product?.product_description ||
                          '-'}
                      </td>
                      <td>{item?.plant?.name ?? '-'}</td>
                      <td>{item?.quantity ?? '-'}</td>
                      <td>{item?.batch ?? '-'}</td>
                      {itemIndex === 0 && (
                        <td rowSpan={rowSpan}>
                          {deliveryObj?.sales_order_number ?? '-'}
                        </td>
                      )}
                    </tr>
                  ));

                  return rows;
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'search' && (
        <div className="div-flex-row cg-20 pagination-container div-align-center">
          <div className="div-flex-row cg-10 div-align-center">
            <button
              className="icon-btn-none"
              onClick={handleDrawerPreviousPage}
              aria-label="Previous page"
              type="button"
            >
              <ArrowLeft className="icon-size-sm" />
            </button>
            <input
              type="number"
              className="page-number-container text-sm"
              value={formattedDrawerPageNumber}
              onChange={handleDrawerPageInputChange}
              aria-label="Page number"
              max={deliveryTotalPages}
              min="1"
            />
            <button
              className="icon-btn-none"
              onClick={handleDrawerNextPage}
              aria-label="Next page"
              type="button"
            >
              <ArrowRight className="icon-size-sm" />
            </button>
          </div>
          <span className="text-grey text-sm">
            Page {drawerSearchPageNumber} of {deliveryTotalPages}
          </span>
        </div>
      )}

      <div className="delivery-footer">
        <button
          type="button"
          className={`vehicle-tracking-detail-add-vehcile-data-btn theme-text-${themeColor} theme-border-color-${themeColor} div-align-center`}
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>

        <button
          type="button"
          className={`vehicle-tracking-detail-move-stage-btn div-align-center theme-background-${themeColor} border-radius-4 vertical-padding-5 horizontal-padding-10 text-white`}
          onClick={handleUpdateDeliverySubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </>
  );
}

export default VehicleTrackingDeliveryEditModal;
